import React, { useRef, useState } from 'react';
import { Camera, Upload, X, Loader2, Image as ImageIcon, Star } from 'lucide-react';
import { toast } from 'sonner';
import { uploadImage } from '../lib/upload';

// Photo gallery field: up to `max` images, the first one is the cover.
const MultiImageUpload = ({ value = [], onChange, folder = 'misc', label, hint, max = 5, testId = 'multi-image-upload', className = '' }) => {
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const cam = useRef(null);
  const gal = useRef(null);
  const images = (value || []).filter(Boolean);
  const full = images.length >= max;

  const pick = async (files) => {
    const list = Array.from(files || []).slice(0, max - images.length);
    if (!list.length) return;
    setBusy(true);
    setPct(0);
    try {
      const urls = [];
      for (const file of list) {
        urls.push(await uploadImage(file, folder, setPct));
      }
      onChange([...images, ...urls].slice(0, max));
      toast.success(urls.length > 1 ? `${urls.length} fotos subidas` : 'Foto subida');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'No se pudo subir la foto');
    } finally {
      setBusy(false);
      setPct(0);
    }
  };

  const remove = (url) => onChange(images.filter((u) => u !== url));
  const makeCover = (url) => onChange([url, ...images.filter((u) => u !== url)]);

  return (
    <div className={className} data-testid={testId}>
      {label && <p className="text-xs font-semibold text-gray-600 mb-1.5">{label} <span className="text-gray-400 font-normal">({images.length}/{max})</span></p>}
      <div className="flex flex-wrap gap-2">
        {images.map((url, idx) => (
          <div key={url} data-testid={`${testId}-item-${idx}`} className={`relative w-24 h-24 rounded-xl overflow-hidden border-2 ${idx === 0 ? 'border-[#EE3B36]' : 'border-gray-200'}`}>
            <img src={url} alt="" className="w-full h-full object-cover" />
            {idx === 0 ? (
              <span className="absolute bottom-0 inset-x-0 bg-[#EE3B36] text-white text-[9px] font-bold text-center py-0.5">PORTADA</span>
            ) : (
              <button type="button" data-testid={`${testId}-cover-${idx}`} onClick={() => makeCover(url)} title="Usar como portada" className="absolute bottom-1 left-1 w-6 h-6 rounded-full bg-black/55 text-white flex items-center justify-center hover:bg-black/80"><Star size={12} /></button>
            )}
            <button type="button" data-testid={`${testId}-remove-${idx}`} onClick={() => remove(url)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"><X size={13} /></button>
          </div>
        ))}
        {!full && (
          <div className="relative w-24 h-24 rounded-xl border border-dashed border-gray-300 bg-[#F7F8FA] flex flex-col items-center justify-center gap-1 text-gray-400">
            {busy ? <><Loader2 size={18} className="animate-spin" /><span className="text-[10px] font-bold">{pct}%</span></> : <><ImageIcon size={22} /><span className="text-[10px]">Agregar</span></>}
          </div>
        )}
      </div>
      <div className="flex gap-2 mt-2 flex-wrap items-center">
        <button type="button" data-testid={`${testId}-camera`} disabled={busy || full} onClick={() => cam.current?.click()} className="h-9 px-3 rounded-xl bg-[#FDECEC] text-[#EE3B36] text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"><Camera size={15} /> Tomar foto</button>
        <button type="button" data-testid={`${testId}-gallery`} disabled={busy || full} onClick={() => gal.current?.click()} className="h-9 px-3 rounded-xl border border-gray-200 text-xs font-bold flex items-center gap-1.5 hover:bg-gray-50 disabled:opacity-50"><Upload size={15} /> Subir fotos</button>
        {hint && <p className="text-[11px] text-gray-400 leading-tight max-w-[260px]">{hint}</p>}
      </div>
      <input data-testid={`${testId}-camera-input`} ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { pick(e.target.files); e.target.value = ''; }} />
      <input data-testid={`${testId}-file-input`} ref={gal} type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={(e) => { pick(e.target.files); e.target.value = ''; }} />
    </div>
  );
};

export default MultiImageUpload;
